<template>
  <EditorStyled>
    <component :is="expandEditor ? 'EditorFullHeight' : 'EditorDefaultHeight'">
      <div
        v-if="props.hasEditAccess"
        class="d-flex flex-column flex-md-row align-center justify-space-between mb-2 py-3 px-3"
        :style="{ backgroundColor: '#fafafa' }"
      >
        <div class="d-flex align-center">
          <!-- Lock toggle placed prominently to the left of SHOW ORIGINAL -->
          <v-tooltip text="AI will skip locked content during regeneration." location="top">
            <template #activator="{ props: tip }">
              <v-btn
                v-bind="tip"
                v-if="props.contentType === 'description' && (props.persistedSource || '').toLowerCase() !== 'missing'"
                class="mr-2"
                size="small"
                variant="tonal"
                :icon="props.descriptionLocked ? 'mdi-lock' : 'mdi-lock-open-variant'"
                :color="props.descriptionLocked ? 'green' : 'grey'"
                :aria-label="props.descriptionLocked ? 'Unlock description' : 'Lock description'"
                :loading="lockLoading"
                :disabled="lockLoading"
                @click="$emit('toggle-lock')"
              />
            </template>
          </v-tooltip>
          <v-btn
            v-if="props.contentType === 'description' && !isSameAsOriginal"
            :disabled="isUpdatingContent"
            variant="plain"
            color="secondary"
            text="show original"
            size="small"
            @click="restoreOriginalDescription"
          />
          <v-btn
            v-else-if="props.contentType === 'description' && isSameAsOriginal"
            variant="plain"
            color="#000"
            text="original"
            disabled
            size="small"
            :style="{ backgroundColor: '#dbdbdb' }"
          />
        </div>
        <div class="d-flex flex-column flex-md-row">
          <v-menu v-model="mainMenu" :disabled="isUpdatingContent" :close-on-content-click="false">
            <template v-slot:activator="{ props }">
              <v-btn
                variant="plain"
                v-bind="props"
                color="#eab308"
                append-icon="mdi-creation"
                :style="{ opacity: 1 }"
                text="AI Enhance"
                >AI</v-btn
              >
            </template>

            <v-card min-width="300">
              <v-list>
                <!-- Tone Selection -->
                <v-menu v-model="toneMenu" open-on-click offset-x :close-on-content-click="false">
                  <template v-slot:activator="{ props: toneProps }">
                    <v-list-item title="Tone" v-bind="toneProps" :subtitle="getToneSubtitle()">
                      <template v-slot:append>
                        <v-icon>mdi-chevron-right</v-icon>
                      </template>
                    </v-list-item>
                  </template>

                  <v-card>
                    <v-list>
                      <v-list-item
                        v-for="tone in toneOptions"
                        :key="tone.value"
                        :title="tone.title === 'Default' ? 'Default (Balanced)' : tone.value"
                        :value="tone.title"
                        @click="selectTone(tone.title)"
                        :class="selectedTone === tone.value ? 'bg-grey-lighten-3' : ''"
                      ></v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>

                <!-- Length Selection (for highlights) -->
                <v-menu
                  v-if="contentType === 'highlights'"
                  v-model="highlightLengthMenu"
                  offset-x
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ props: highlightLengthProps }">
                    <v-list-item
                      title="Length Type"
                      v-bind="highlightLengthProps"
                      :subtitle="getHighlightLengthSubtitle()"
                    >
                      <template v-slot:append>
                        <v-icon>mdi-chevron-right</v-icon>
                      </template>
                    </v-list-item>
                  </template>

                  <v-card>
                    <v-list>
                      <v-list-item
                        title="Character Limit"
                        value="characters"
                        @click="selectHighlightLengthType('characters')"
                        :class="highlightLengthType === 'characters' ? 'bg-grey-lighten-3' : ''"
                      ></v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>

                <!-- Character Count Input (for highlights) -->
                <v-list-item v-if="contentType === 'highlights'">
                  <div class="d-flex align-center">
                    <span class="mr-3">Characters:</span>
                    <v-text-field
                      v-model="characterCount"
                      type="number"
                      variant="outlined"
                      density="compact"
                      hide-details
                      class="length-count-input"
                      bg-color="grey-lighten-4"
                      :style="{ width: '120px' }"
                      min="10"
                      max="50"
                    />
                  </div>
                </v-list-item>

                <!-- Length Selection (for descriptions) -->
                <v-menu
                  v-if="contentType === 'description'"
                  v-model="lengthMenu"
                  offset-x
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ props: lengthProps }">
                    <v-list-item
                      title="Length"
                      v-bind="lengthProps"
                      :subtitle="getLengthSubtitle()"
                    >
                      <template v-slot:append>
                        <v-icon>mdi-chevron-right</v-icon>
                      </template>
                    </v-list-item>
                  </template>

                  <v-card>
                    <v-list>
                      <template v-for="lengthOpt in lengthOptions">
                        <v-list-item
                          v-if="hasContent || (lengthOpt.title !== 'Longer' && lengthOpt.title !== 'Shorter')"
                          :key="lengthOpt.value"
                          :title="lengthOpt.title === 'Default' ? 'Default (~200 words)' : lengthOpt.value"
                          :value="lengthOpt.title"
                          @click="selectLength(lengthOpt.title)"
                          :class="selectedLength === lengthOpt.value ? 'bg-grey-lighten-3' : ''"
                        ></v-list-item>
                      </template>
                    </v-list>
                  </v-card>
                </v-menu>

                <!-- Word Count Range Inputs (for descriptions) -->
                <v-list-item v-if="contentType === 'description' && selectedLength === 'WordCount'">
                  <v-row class="pa-2" no-gutters>
                    <v-col cols="5">
                      <v-text-field
                        v-model="minWordCount"
                        label="Min"
                        type="number"
                        variant="outlined"
                        density="compact"
                        hide-details
                        class="word-count-input"
                        bg-color="grey-lighten-4"
                        :style="{ width: '100px' }"
                      />
                    </v-col>

                    <v-col cols="1" class="d-flex justify-center align-center">
                      <span class="text-grey">-</span>
                    </v-col>

                    <v-col cols="5">
                      <v-text-field
                        v-model="maxWordCount"
                        label="Max"
                        type="number"
                        variant="outlined"
                        density="compact"
                        hide-details
                        class="word-count-input"
                        bg-color="grey-lighten-4"
                        :style="{ width: '100px' }"
                      />
                    </v-col>
                  </v-row>
                </v-list-item>

                <!-- Number of Bullets Selection (for highlights) -->
                <v-menu
                  v-if="contentType === 'highlights'"
                  v-model="bulletsMenu"
                  offset-x
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ props: bulletsProps }">
                    <v-list-item
                      title="Number of bullets"
                      v-bind="bulletsProps"
                      :subtitle="`${numberOfBullets} bullets`"
                    >
                      <template v-slot:append>
                        <v-icon>mdi-chevron-right</v-icon>
                      </template>
                    </v-list-item>
                  </template>

                  <v-card>
                    <v-list>
                      <v-list-item
                        v-for="bulletCount in bulletOptions"
                        :key="bulletCount"
                        :title="`${bulletCount} bullets`"
                        :value="bulletCount"
                        @click="selectBullets(bulletCount)"
                        :class="numberOfBullets === bulletCount ? 'bg-grey-lighten-3' : ''"
                      ></v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>

                <!-- Format Menu (for descriptions) -->
                <v-menu
                  v-if="contentType === 'description'"
                  v-model="formatMenu"
                  offset-x
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ props: formatProps }">
                    <v-list-item
                      title="Format"
                      v-bind="formatProps"
                      :subtitle="getFormatSubtitle()"
                    >
                      <template v-slot:append>
                        <v-icon>mdi-chevron-right</v-icon>
                      </template>
                    </v-list-item>
                  </template>

                  <v-card>
                    <v-list>
                      <v-list-item
                        v-for="format in formatOptions"
                        :key="format.title"
                        :title="format.title === 'Default' ? 'Default (Paragraphs)' : format.value"
                        :value="format.title"
                        @click="selectFormat(format.title)"
                        :class="selectedFormat === format.value ? 'bg-grey-lighten-3' : ''"
                      ></v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>

                <!-- Prompt Menu -->
                <v-menu v-model="promptMenu" offset-x :close-on-content-click="false">
                  <template v-slot:activator="{ props: promptProps }">
                    <v-list-item
                      title="Custom Prompt"
                      v-bind="promptProps"
                      :subtitle="getSelectedPromptTitle()"
                    >
                      <template v-slot:append>
                        <v-icon>mdi-chevron-right</v-icon>
                      </template>
                    </v-list-item>
                  </template>

                  <v-card>
                    <v-list>
                      <v-list-item
                        v-for="option in promptOptions"
                        :key="option.value || 'default'"
                        :title="option.title"
                        :value="option.value"
                        @click="selectPrompt(option.value)"
                        :class="selectedPromptId === option.value ? 'bg-grey-lighten-3' : ''"
                      ></v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
              </v-list>

              <v-card-actions class="d-flex flex-column">
                <!-- Revise Existing -->
                <v-btn
                  v-if="getEditorContent() !== ''"
                  block
                  color="primary"
                  variant="outlined"
                  :onclick="reviseAiDescription"
                  >Revise</v-btn
                >

                <!-- Generate New -->
                <v-btn
                  block
                  color="primary"
                  variant="flat"
                  :onclick="generateNewAiDescription"
                  >Generate New</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-menu>
          <v-divider vertical class="mx-1" />
          <v-btn
            :disabled="!canRevert || isUpdatingContent"
            variant="plain"
            color="secondary"
            prepend-icon="mdi-restore"
            aria-label="Revert Changes"
            @click="revertChanges()"
            text="Revert Changes"
          />
          <v-btn
            :loading="props.isSaving"
            :disabled="!enableSave || isUpdatingContent"
            variant="plain"
            color="primary"
            @click="saveChanges(getEditorContent())"
            text="Save Changes"
          />
        </div>
      </div>
      <v-divider horizontal class="mx-1" />
      <EditorToolbar
        v-if="editor && props.hasEditAccess && props.contentType === 'description'"
        :isDisabled="props.isUpdatingContent"
        :editor="editor"
        :vehicle="props.vehicle"
        :isOpen="showToolbar"
        :setIsOpen="setShowToolbar"
      />
      <v-divider horizontal class="mx-1" />
      <description-skeleton v-if="props.isUpdatingContent" />
      <EditorContent v-else :editor="editor" class="description-editor pa-3 rounded-lg" />
    </component>
  </EditorStyled>
  <!-- Friendly error message for generation failures -->
  <v-snackbar v-model="showGenError" color="error" timeout="5000" location="bottom right">
    {{ genErrorMessage }}
  </v-snackbar>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, nextTick } from 'vue'
import { useEditor, EditorContent, Editor } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Highlight from '@tiptap/extension-highlight'
import Typography from '@tiptap/extension-typography'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Superscript from '@tiptap/extension-superscript'
import Subscript from '@tiptap/extension-subscript'
import EditorToolbar from './EditorToolbar.vue'
import EditorStyled from './EditorStyled.vue'
import TabExtension from './extensions/TabExtension'
import { fetchNewVehicleDescription } from '@/apis'
import {
  type IDescriptions,
  type AiDescriptionChangeRequest,
  type IGenerateDescriptionRequestParams,
  GenerationType,
} from '@/types'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { usePromptsStore } from '@/stores/prompts'
import { getEmptyDescriptionsTemplate } from '@/utils'
import { Format, Length, Tone } from '@/types'
import DescriptionSkeleton from '../DescriptionSkeleton.vue'

interface Props {
  content: string
  descriptions: IDescriptions
  enableSave: boolean
  expandEditor: boolean
  userEdited: boolean
  vehicle: any
  isUpdatingContent: boolean
  isSaving: boolean
  saveChanges: (value: string) => void
  source: string
  persistedSource?: string
  contentType?: 'description' | 'highlights'
  availablePrompts: Array<{ id: string; name: string; content: string }>
  defaultFormat?: keyof typeof Format
  hasEditAccess?: boolean
  descriptionLocked?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  contentType: 'description',
  defaultFormat: 'Default',
  hasEditAccess: true,
  persistedSource: 'missing',
})
const accountInfo = useAccountSettingsStore()
const promptsStore = usePromptsStore()

const emit = defineEmits<{
  (e: 'update', content: string): void
  (e: 'set-user-edited', value: boolean): void
  (e: 'set-source', value: string): void
  (e: 'set-temp-description', content: string): void
  (e: 'set-enable-save', content: boolean): void
  (e: 'set-updating-content', content: boolean): void
  (e: 'toggle-lock'): void
}>()
// Receive lock loading state from parent via provide/inject alternative
// Simpler: infer loading by listening for toggles? We'll accept a prop via attribute binding
const lockLoading = ref(false)
// Parent can set via attribute binding on the component; Vue will pass as HTML attribute
// but to keep it deterministic, we watch for custom DOM attr not needed; instead, we expose a method
// Merge exposed methods into a single defineExpose call at the bottom.

const userEdited = computed(() => props.userEdited)
const showToolbar = ref(props.userEdited)
const mainMenu = ref(false)
const descriptions = computed<IDescriptions>(() => props.descriptions)
// const tempDescription = computed(() => props.content ?? '')
const enableSave = computed(() => {
  const current = getEditorContent()
  if (props.contentType === 'description') {
    // For descriptions, compare against the session baseline exactly (raw HTML)
    return (baselineHtml.value || '') !== current
  }
  // For highlights, continue comparing by normalized plain text
  return toPlainText(descriptions.value.default.highlight ?? '') !== toPlainText(current)
})

// Baseline for revert feature
const baselineContent = computed(() =>
  props.contentType === 'description'
    ? baselineHtml.value
    : descriptions.value.default?.highlight ?? '',
)

const canRevert = computed(() => {
  const current = getEditorContent()
  if (props.contentType === 'description') {
    // Revert available when current differs from session baseline
    return (baselineHtml.value || '') !== current
  }
  return toPlainText(baselineContent.value ?? '') !== toPlainText(current)
})

const retryCount = ref(0)
const retryLimit = 1

// Friendly error snackbar state
const showGenError = ref(false)
const genErrorMessage = ref('')
const showGenerationError = (msg?: string) => {
  genErrorMessage.value = msg || 'Unfortunately, generation failed. Please try again.'
  showGenError.value = true
}

const editorContent = computed(() => props.content ?? '')
const source = computed(() => props.source ?? '')

// Session baseline: capture the editor's initial HTML and refresh on demand
const baselineHtml = ref('')
const ai2BaselineHtml = ref('')

// Enable/disable Save strictly by comparing against the session baseline (raw HTML)
const isSameAsBaseline = computed(() => (baselineHtml.value || '') === getEditorContent())

// Canonicalize HTML by using a detached TipTap Editor instance (no DOM binding)
const canonicalize = (html: string): string => {
  try {
    const tempEditor = new Editor({
      content: html || '',
      extensions: editorExtensions as any,
      editable: false,
    })
    const normalized = tempEditor.getHTML()
    tempEditor.destroy()
    return normalized
  } catch {
    return html || ''
  }
}

// Original (A5) canonical baseline and equality flag
const originalBaselineHtml = ref('')
const isSameAsOriginal = computed(() => {
  if (props.contentType !== 'description') return false
  // Normalize '<p></p>' and '' to the same empty state on BOTH sides
  const normalizeEmpty = (html: string) => (html === '<p></p>' ? '' : html || '')
  const original = normalizeEmpty(originalBaselineHtml.value)
  const current = normalizeEmpty(getEditorContent())
  return current === original
})

// Expose a method so parent can refresh baseline after a successful save
const resetBaseline = (): void => {
  baselineHtml.value = getEditorContent()
}
defineExpose({
  resetBaseline,
  setLockLoading: (v: boolean) => (lockLoading.value = v),
})
const ai2BaselineHighlightText = ref('')

// Debug: central logger for enable states
const logEnableStates = (reason: string) => {
  try {
    const baseline = props.contentType === 'description'
      ? baselineHtml.value
      : descriptions.value.default?.highlight ?? ''
    const current = getEditorContent()
    const baselineText = props.contentType === 'highlights' ? toPlainText(baseline ?? '') : undefined
    const currentText = props.contentType === 'highlights' ? toPlainText(current) : undefined
    const equalsText = props.contentType === 'highlights' ? baselineText === currentText : undefined
    console.log('[Editor Evaluate]', {
      reason,
      contentType: props.contentType,
      enableSave: enableSave.value,
      canRevert: props.contentType === 'description' ? !isSameAsBaseline.value : canRevert.value,
      baseline,
      current,
      baselineText,
      currentText,
      equalsText,
    })
  } catch (e) {
    console.warn('[Editor Evaluate] logging failed', e)
  }
}

// Remove DIV wrappers for highlights comparison without losing inline formatting
const stripDivs = (html: string): string => {
  if (!html) return ''
  const container = document.createElement('div')
  container.innerHTML = html
  container.querySelectorAll('div').forEach((div) => {
    const fragment = document.createDocumentFragment()
    while (div.firstChild) fragment.appendChild(div.firstChild)
    div.replaceWith(fragment)
  })
  return container.innerHTML.replace(/>\s+</g, '><').trim()
}

// Extract plain text (collapse whitespace) – used for highlights comparisons
const toPlainText = (html: string): string => {
  let h = html || ''
  // Ensure block boundaries contribute a space when flattened
  h = h.replace(/<\/(p|li|div|ul|ol)>/gi, '</$1> ')
  const div = document.createElement('div')
  div.innerHTML = h
  let text = (div.textContent || div.innerText || '')
  // Normalize punctuation spacing to a single trailing space
  text = text.replace(/\s*([.,;:!?])\s*/g, '$1 ')
  // Collapse remaining whitespace
  return text.replace(/\s+/g, ' ').trim()
}

const aiDescriptionChangeRequest = ref<AiDescriptionChangeRequest>({
  tones: [],
  lengths: [],
  formats: [],
  minWordCount: '',
  maxWordCount: '',
})

const setShowToolbar = (value: boolean) => {
  showToolbar.value = value
}

// Define types that represent the keys of your enums
type ToneKey = keyof typeof Tone
type LengthKey = keyof typeof Length
type FormatKey = keyof typeof Format

// Tone Selection
const toneMenu = ref(false)
const selectedTone = ref<ToneKey>(Tone.Default)
const toneOptions = (Object.keys(Tone) as Array<keyof typeof Tone>).map((key) => ({
  value: Tone[key],
  title: key,
}))
const selectTone = (tone: ToneKey) => {
  selectedTone.value = tone
  // Force the tone menu to close after a brief delay
  // This ensures the click event is fully processed
  setTimeout(() => {
    toneMenu.value = false
  }, 10)
}

// Tone subtitle helper to show friendly default label
const getToneSubtitle = () =>
  selectedTone.value === 'Default' ? 'Default (Balanced)' : Tone[selectedTone.value]

// Length Selection
const lengthMenu = ref(false)
const minWordCount = ref('100')
const maxWordCount = ref('300')
const selectedLength = ref<LengthKey>(Length.Default)
const lengthOptions = (Object.keys(Length) as Array<keyof typeof Length>).map((key) => ({
  value: Length[key],
  title: key,
}))
const selectLength = (length: LengthKey) => {
  selectedLength.value = length
  setTimeout(() => {
    lengthMenu.value = false
  }, 10)
}
const getLengthSubtitle = () => {
  if (selectedLength.value === 'WordCount') {
    return `${minWordCount.value}-${maxWordCount.value} words`
  }
  return selectedLength.value === 'Default' ? 'Default (~200 words)' : Length[selectedLength.value]
}

// Character Count for highlights
const defaultCharacterCount = '40'
const characterCount = ref(defaultCharacterCount)

// Highlight length type selection (characters only)
const highlightLengthMenu = ref(false)
const highlightLengthType = ref<'characters'>('characters')

const selectHighlightLengthType = (type: 'characters') => {
  highlightLengthType.value = type
  setTimeout(() => {
    highlightLengthMenu.value = false
  }, 10)
}

const getHighlightLengthSubtitle = () => {
  return `${characterCount.value} characters`
}

// Number of Bullets for highlights
const bulletsMenu = ref(false)
const numberOfBullets = ref(3)
const bulletOptions = Array.from({ length: 5 }, (_, i) => i + 1) // 1 to 10
const selectBullets = (count: number) => {
  numberOfBullets.value = count
  setTimeout(() => {
    bulletsMenu.value = false
  }, 10)
}

// Revert changes
const revertChanges = (): void => {
  const baseline = baselineContent.value
  editor.value?.commands.setContent(baseline || '')
  emit('update', baseline || '')
  if (props.contentType === 'description') {
    const html = editor.value?.getHTML() || ''
    if ((html || '').trim() === '') {
      emit('set-source', 'missing')
    } else if (html === originalBaselineHtml.value) {
      emit('set-source', 'provided')
    } else if (html === ai2BaselineHtml.value) {
      emit('set-source', 'ai2')
    } else {
      emit('set-source', 'user-provided')
    }
  }
  emit('set-enable-save', false)
  emit('set-user-edited', false)
}

// Format Selection
const formatMenu = ref(false)
const selectedFormat = ref<FormatKey>(props.defaultFormat as FormatKey)
const formatOptions = (Object.keys(Format) as Array<keyof typeof Format>).map((key) => ({
  value: Format[key],
  title: key,
}))
const selectFormat = (format: FormatKey) => {
  selectedFormat.value = format
  setTimeout(() => {
    formatMenu.value = false
  }, 10)
}

// Format subtitle helper to show friendly default label
const getFormatSubtitle = () =>
  selectedFormat.value === 'Default' ? 'Default (Paragraphs)' : Format[selectedFormat.value]

// Prompt Selection
const promptMenu = ref(false)
const selectedPromptId = ref<string | null>(null)

// Use available prompts from props
const availablePrompts = computed(() => props.availablePrompts || [])

// Get prompt selection options
const promptOptions = computed(() => [
  { value: null, title: 'Default' },
  ...availablePrompts.value.map((prompt) => ({
    value: prompt.id,
    title: prompt.name,
  })),
])

const selectPrompt = (promptId: string | null) => {
  selectedPromptId.value = promptId

  // Update the store selection based on content type
  if (props.contentType === 'description') {
    promptsStore.setSelectedDescriptionPrompt(promptId)
  } else {
    promptsStore.setSelectedHighlightPrompt(promptId)
  }

  setTimeout(() => {
    promptMenu.value = false
  }, 10)
}

// Get the selected prompt for display
const getSelectedPromptTitle = () => {
  if (!selectedPromptId.value) return 'Default'
  const prompt = availablePrompts.value.find((p) => p.id === selectedPromptId.value)
  return prompt?.name || 'Default'
}

// Content presence flag for conditional UI (e.g., hiding Longer/Shorter, Revise)
const hasContent = computed(() => getEditorContent() !== '')

// Initialize selected prompt from store
const initializeSelectedPrompt = () => {
  if (props.contentType === 'description') {
    selectedPromptId.value = promptsStore.selectedDescriptionPromptId
  } else {
    selectedPromptId.value = promptsStore.selectedHighlightPromptId
  }
}

// Initialize on component mount
initializeSelectedPrompt()

const generateNewAiDescription = async (): Promise<void> => {
  emit('set-updating-content', true)
  emit('set-source', 'ai2')
  mainMenu.value = false

  try {
    // Prepare request parameters
    const accountId = accountInfo.accountId
    const vin = props.vehicle?.vin
    let aiDescriptionChangeRequest: AiDescriptionChangeRequest

    if (props.contentType === 'highlights') {
      // For highlights, use descriptive values that include context
      const lengthText = `under ${characterCount.value} characters per bullet point`

      aiDescriptionChangeRequest = {
        tones: [Tone[selectedTone.value as ToneKey]],
        lengths: [lengthText], // Include context about the type of limit
        formats: [`${numberOfBullets.value} bullet points only, no paragraph text`], // Always specify bullet points for highlights
        minWordCount: '',
        maxWordCount: '',
        highlightBulletCount: String(numberOfBullets.value),
        highlightCharacterLimit: String(characterCount.value),
      }
    } else {
      // For descriptions, use the original format
      aiDescriptionChangeRequest = {
        tones: [Tone[selectedTone.value as ToneKey]],
        lengths: [Length[selectedLength.value as LengthKey]],
        formats: [Format[selectedFormat.value as FormatKey]],
        minWordCount: minWordCount.value,
        maxWordCount: maxWordCount.value,
      }
    }

    if (!vin) {
      console.error('Failed generating content. VIN is not available')
      return
    }

    // Get the selected prompt content
    let promptContent = ''
    if (selectedPromptId.value) {
      // Custom prompt selected - fetch content from store
      const selectedPrompt = availablePrompts.value.find(
        (p: any) => p.id === selectedPromptId.value,
      )
      if (selectedPrompt) {
        promptContent = selectedPrompt.content
      }
    }

    const apiCallParams: IGenerateDescriptionRequestParams = {
      accountId,
      vin,
      promptText: promptContent, // Send as promptText instead of content
      format: aiDescriptionChangeRequest,
      generationType:
        props.contentType === 'highlights' ? GenerationType.Highlight : GenerationType.Description,
      originalContent: getEditorContent(),
      intent: 'new',
    }
    const desc = await fetchNewVehicleDescription(apiCallParams)
    let errorMessage = ''

    if (desc.message != null) {
      errorMessage = String(desc.message)
      console.error('Service error:', desc.message)
    }

    // retry when service is unavailable
    if (errorMessage !== '') {
      if (errorMessage.toLowerCase().includes('unavailable') && retryCount.value < retryLimit) {
        console.error(`retrying ${retryCount.value + 1} of ${retryLimit}`)
        retryCount.value++
        return await generateNewAiDescription() // ⚠️ WARNING: recursive call
      } else {
        showGenerationError(errorMessage)
      }
    } else {
      retryCount.value = 0 // reset retry count on success
      // tempDescription.value = desc.ai2.value
      const newContent = props.contentType === 'highlights'
        ? desc.ai2.highlight
        : (desc.ai2.description || desc.default?.description || desc.a5?.description || desc.ai2.value || desc.default?.value || desc.a5?.value)
      editor.value?.commands.setContent(newContent || '')
      emit('update', newContent || '')
      if (props.contentType === 'highlights') {
        ai2BaselineHighlightText.value = toPlainText(newContent || '')
      }
    }
  } catch (err: any) {
    console.error('Generation error', err)
    showGenerationError('Unfortunately, generation failed. Please try again.')
  } finally {
    emit('set-updating-content', false)
  }
}

const reviseAiDescription = async (): Promise<void> => {
  emit('set-updating-content', true)
  emit('set-source', 'ai2')
  mainMenu.value = false

  try {
    const accountId = accountInfo.accountId
    const vin = props.vehicle?.vin
    let aiDescriptionChangeRequest: AiDescriptionChangeRequest

    if (props.contentType === 'highlights') {
      // For highlights, use descriptive values that include context
      const lengthText = `${characterCount.value} under characters per bullet point`

      aiDescriptionChangeRequest = {
        tones: [Tone[selectedTone.value]],
        lengths: [lengthText], // Include context about the type of limit
        formats: [`${numberOfBullets.value} bullet points only, no paragraph text`], // Always specify bullet points for highlights
        minWordCount: '',
        maxWordCount: '',
        highlightBulletCount: String(numberOfBullets.value),
        highlightCharacterLimit: String(characterCount.value),
      }
    } else {
      // For descriptions, use the original format
      aiDescriptionChangeRequest = {
        tones: [Tone[selectedTone.value]],
        lengths: [Length[selectedLength.value]],
        formats: [Format[selectedFormat.value]],
        minWordCount: minWordCount.value,
        maxWordCount: maxWordCount.value,
      }
    }

    if (!vin) {
      console.error('Failed generating content. VIN is not available')
      return
    }

    // Editor content to revise + optional custom prompt
    const originalContent = getEditorContent() // Use getEditorContent() instead of editorContent.value
    let promptContent = ''
    if (selectedPromptId.value) {
      const selectedPrompt = availablePrompts.value.find(
        (p: any) => p.id === selectedPromptId.value,
      )
      if (selectedPrompt) {
        promptContent = selectedPrompt.content
      }
    }

    const apiCallParams: IGenerateDescriptionRequestParams = {
      accountId,
      vin,
      promptText: promptContent,
      originalContent,
      format: aiDescriptionChangeRequest,
      generationType:
        props.contentType === 'highlights' ? GenerationType.Highlight : GenerationType.Description,
      intent: 'revise',
    }
    const desc = await fetchNewVehicleDescription(apiCallParams)
    let errorMessage = ''

    if (desc.message != null) {
      errorMessage = 'Unfortunately, generation failed. Please try again.'
      console.error('Service error:', desc.message)
    }

    // retry when service is unavailable
    if (errorMessage !== '') {
      if (errorMessage.toLowerCase().includes('unavailable') && retryCount.value < retryLimit) {
        console.error(`retrying ${retryCount.value + 1} of ${retryLimit}`)
        retryCount.value++
        return await generateNewAiDescription() // ⚠️ WARNING: recursive call
      } else {
        showGenerationError(errorMessage)
      }
    } else {
      retryCount.value = 0 // reset retry count on success
      // descriptions.value = desc
      // tempDescription.value = desc.ai2.value
      const newContent = (
        props.contentType === 'highlights'
          ? (desc.ai2.highlight || desc.a5.description)
          : (desc.ai2.description || desc.default?.description || desc.a5?.description)
      ) || ''
      editor.value?.commands.setContent(newContent)
      emit('update', newContent)
      if (props.contentType === 'highlights') {
        ai2BaselineHighlightText.value = toPlainText(newContent || '')
      }
    }
  } catch (err: any) {
    console.error('Generation error', err)
    showGenerationError('Unfortunately, generation failed. Please try again.')
  } finally {
    emit('set-updating-content', false)
  }
}

const restoreOriginalDescription = async (): Promise<void> => {
  const original = descriptions.value.a5.description || descriptions.value.a5.value || ''

  emit('set-source', 'provided')
  emit('update', original)

  editor.value?.commands.setContent(original)
}

const getEditorContent = () => {
  let content = editor.value?.getHTML() ?? ''

  if (content === '<p></p>') {
    content = ''
  }

  return content
}

const editorExtensions = [
  StarterKit,
  Highlight,
  Typography,
  Underline,
  Link,
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  Superscript,
  Subscript,
  TabExtension,
]

const editor = useEditor({
  content: editorContent.value,
  extensions: editorExtensions as any,
  editable: props.hasEditAccess,
  onFocus: () => {
    setShowToolbar(true)
  },
  onUpdate: ({ editor }) => {
    const html = editor.getHTML()
    emit('update', html)

    // Determine source based on current content vs baselines
    if (props.contentType === 'description') {
      const isEmpty = html === '' || html === '<p></p>'

      if (isEmpty) {
        if (source.value !== 'missing') emit('set-source', 'missing')
        return
      }

      // Capture AI baseline the first time we see ai2 content in this session
      if (source.value === 'ai2' && !ai2BaselineHtml.value) {
        ai2BaselineHtml.value = html
      }

      if (html === originalBaselineHtml.value) {
        if (source.value !== 'provided') emit('set-source', 'provided')
      } else if (ai2BaselineHtml.value && html === ai2BaselineHtml.value) {
        if (source.value !== 'ai2') emit('set-source', 'ai2')
      } else {
        if (source.value !== 'user-provided') emit('set-source', 'user-provided')
      }
    } else if (props.contentType === 'highlights') {
      const currentText = toPlainText(html)
      const baseline = descriptions.value.default?.highlight ?? ''
      const baselineText = toPlainText(baseline)

      if (currentText === '') {
        if (source.value !== 'none') emit('set-source', 'none')
      } else if (ai2BaselineHighlightText.value && currentText === ai2BaselineHighlightText.value) {
        if (source.value !== 'ai2') emit('set-source', 'ai2')
      } else if (currentText !== baselineText) {
        if (source.value !== 'user-provided') emit('set-source', 'user-provided')
      }
    }
  },
})

// watch(
//   () => props.userEdited,
//   (val) => {
//     showToolbar.value = val
//   },
// )

// watch(
//     () => descriptions.value,
//     (val: any) => emit('set-descriptions', val)
// )

watch(
  () => editorContent.value,
  (val: any) => {
    // When upstream content changes (vehicle switch), reset baseline to that content
    baselineHtml.value = val || ''
    editor.value?.commands.setContent(baselineHtml.value)
    // Canonicalize baseline to TipTap's normalized HTML
    baselineHtml.value = editor.value?.getHTML() || baselineHtml.value
    // Reset AI2 baseline on vehicle/content swap
    ai2BaselineHtml.value = ''
    ai2BaselineHighlightText.value = ''
    // emit('set-temp-description', val)
    logEnableStates('editorContent changed')
  },
)

watch(
  () => descriptions.value,
  (val: any) => {
    if (props.contentType === 'description') {
      originalBaselineHtml.value = canonicalize(
        descriptions.value.a5?.description ?? descriptions.value.a5?.value ?? '',
      )
      ai2BaselineHtml.value = canonicalize(descriptions.value.ai2?.description ?? '')
    }
    if (props.contentType === 'highlights') {
      ai2BaselineHighlightText.value = ''
    }
    logEnableStates('descriptions changed')
  },
)

onMounted(async () => {
  await nextTick()
  // Initialize baseline from the provided content prop first,
  // then sync editor to that content for a stable initial equality
  baselineHtml.value = editorContent.value || ''
  editor.value?.commands.setContent(baselineHtml.value)
  // Canonicalize baseline to TipTap's normalized HTML to avoid false diffs
  baselineHtml.value = editor.value?.getHTML() || baselineHtml.value
  if (props.contentType === 'description') {
    originalBaselineHtml.value = canonicalize(
      descriptions.value.a5?.description ?? descriptions.value.a5?.value ?? '',
    )
    ai2BaselineHtml.value = canonicalize(descriptions.value.ai2?.description ?? '')
  }
  logEnableStates('mounted')
})

watch(
  () => enableSave.value,
  (val: any) => {
    const baseline = props.contentType === 'description'
      ? baselineHtml.value
      : descriptions.value.default.highlight
    console.log('[Editor Save Enable]', {
      contentType: props.contentType,
      enabled: val,
      baseline,
      current: getEditorContent(),
    })
    emit('set-enable-save', val)
  },
)

// Log when revert enablement changes
watch(
  () => canRevert.value,
  (val: any) => {
    const baseline = props.contentType === 'description'
      ? baselineHtml.value
      : descriptions.value.default.highlight
    console.log('[Editor Revert Enable]', {
      contentType: props.contentType,
      enabled: val,
      baseline,
      current: getEditorContent(),
      baselineText: props.contentType === 'highlights' ? toPlainText(baseline ?? '') : undefined,
      currentText: props.contentType === 'highlights' ? toPlainText(getEditorContent()) : undefined,
    })
  },
)

// Keep editor editability in sync with access changes
watch(
  () => props.hasEditAccess,
  (hasAccess: boolean) => {
    if (editor.value) {
      editor.value.setEditable(hasAccess)
    }
  },
)
</script>

<style scoped>
.description-editor :deep(.ProseMirror) {
  box-shadow: none;
  outline: none;
}
</style>
